import { useDeviceType } from "@dash/mq";
import { Pagination } from "@dash/search";
import { ErrorScreen } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { CSSObject, useTheme } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom-v5-compat";

import { CubeCollapsedTileSkeletonStack } from "./CubeCollapsedTileSkeletonStack";
import { cubeFactTablePageSize } from "./cubeFactTableLayout";
import { buildNoRegionPostalDisplayLabel, CustomerFactRow } from "./customersViewFactsData";
import { UnassignedProCubeTable } from "./UnassignedProCubeTable";

import type { UnassignedProRow } from "./unassignedProFactsData";

const pageSize = cubeFactTablePageSize;

const firstColumnCellPadding: CSSObject = { padding: "0.75rem" };

type PropsUnassigned = {
  variant: "unassignedPro";
  rows: UnassignedProRow[];
  error: Error | null;
  isLoading: boolean;
};

type PropsPostal = {
  variant: "noRegionPostal";
  rows: CustomerFactRow[];
  error: Error | null;
  isLoading: boolean;
};

type Props = PropsUnassigned | PropsPostal;

export const CustomerCubeFactTable: React.FC<Props> = (props) => {
  if (props.variant === "unassignedPro") {
    return <UnassignedProCubeTable rows={props.rows} error={props.error} isLoading={props.isLoading} />;
  }

  const theme = useTheme();
  const { isMobile } = useDeviceType();
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [props.rows.length]);

  if (props.error) {
    return <ErrorScreen>{props.error.message}</ErrorScreen>;
  }
  if (props.isLoading) {
    return <CubeCollapsedTileSkeletonStack />;
  }

  const rows = props.rows;
  if (rows.length === 0) {
    return <div css={{ color: theme.colors.secondaryText, fontSize: "0.875rem" }}>Brak danych.</div>;
  }

  const tableColumns: TableHeadColumn<string>[] = [
    { label: "Nazwa", isSortable: false },
    { label: "Kod pocztowy", isSortable: false },
  ];

  const sliceStart = page * pageSize;
  const pageRows = rows.slice(sliceStart, sliceStart + pageSize);
  const paddedRows: (CustomerFactRow | null)[] = [...pageRows];
  while (paddedRows.length < pageSize) {
    paddedRows.push(null);
  }

  const bodyRows = paddedRows.map((row, index) => {
    if (row === null) {
      return {
        id: `pad-postal-${sliceStart}-${index}`,
        cellsContent: [
          {
            element: <span css={firstColumnCellPadding}>{"\u00a0"}</span>,
          },
          { element: "\u00a0" },
        ],
      };
    }
    const label = buildNoRegionPostalDisplayLabel(row);
    const uid = row.uuid.trim();
    const nameElement =
      uid.length > 0 ? (
        <Link
          to={`/crm/customers/id/${uid}`}
          css={{
            color: theme.colors.primary,
            fontWeight: 500,
            textDecoration: "none",
            ":hover": { textDecoration: "underline" },
          }}
        >
          {label}
        </Link>
      ) : (
        label
      );
    const nameCell = <span css={firstColumnCellPadding}>{nameElement}</span>;
    const postal = row.postalCode.trim();
    const cellsContent: RowCellContent[] = [
      { element: nameCell },
      { element: postal.length > 0 ? postal : "—" },
    ];
    return {
      id: uid.length > 0 ? uid : `postal-${sliceStart + index}`,
      cellsContent,
    };
  });

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <Table columns={tableColumns} css={{ maxWidth: isMobile ? "100%" : "48rem" }}>
        <TableBody isLoading={false} rows={bodyRows} />
      </Table>
      <Pagination
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        itemCount={rows.length}
      />
    </div>
  );
};
